/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect } from 'react';
import { isEqual } from 'lodash';
import { Button } from 'react-bootstrap';

import { config, columns, defaultSorts, attributionFilters } from './config';
import PageHeader from '../../components/PageHeader';
import AttributionFilters from './AttributionFilters';
import TitleBanner from '../../components/TitleBanner';
import { extractFilterValues } from '../../utils/filters';
import Table from '../../components/Table';
import InlineQuery from '../../components/InlineQuery';

const AttributionPlatform = ({
  blocked,
  filters,
  queries,
  filtersActions: { getFiltersForPageAsync, updateFilters, clearFilters },
  queryActions: { runFormattedInlineQueriesForPage },
  segmentActions: { openAudienceSegmentWindow },
  allowedCountries
}) => {
  const [selectedFilters, setSelectedFilters] = useState(
    extractFilterValues(filters.fields)
  );
  const [showTable, setShowTable] = useState(false);
  const [selectedTitles, setSelectedTitles] = useState([]);

  const updateQueries = () => {
    if (showTable) {
      const filterOverrides = {};
      runFormattedInlineQueriesForPage(config.ATTRIBUTION_PLATFORM_FILTERS, {
        ...filters.fields,
        ...filterOverrides
      });
    }
  };

  const resetSelectedTitles = () => {
    // Clear the selected titles and trigger an event to update state
    localStorage.setItem('attributionSelectedTitles', JSON.stringify([]));
    const event = new Event('attributionSelectedTitlesUpdated');
    document.dispatchEvent(event);
  };

  useEffect(() => {
    resetSelectedTitles();
  }, [queries.attributionPlatform]);

  useEffect(() => {
    if (!blocked) {
      const values = extractFilterValues(filters.fields);
      const hasNewValues = !isEqual(values, selectedFilters);
      const contentFields = attributionFilters.content;
      let hasContentValues = false;
      values.forEach(v => {
        const [k] = v.split(':');
        if (contentFields.filter(cf => cf.id === k).length > 0) {
          hasContentValues = true;
        }
      });
      setShowTable(hasContentValues);

      if (hasNewValues) {
        setSelectedFilters(values);
        updateQueries();
      }

      if (
        selectedFilters?.length === 0 &&
        !Object.keys(filters.fields).length
      ) {
        // handle "clear all" from TabbedFilter component
        getFiltersForPageAsync(config.ATTRIBUTION_PLATFORM_PAGE_ID);
      }
    }
  }, [filters.fields]);

  useEffect(() => {
    updateQueries();
  }, [showTable]);

  // eslint-disable-next-line
  useEffect(() => {
    getFiltersForPageAsync(config.ATTRIBUTION_PLATFORM_PAGE_ID);
    document.title = 'Platform Attribution | CVM Insights';
  }, []);

  // Listen for changes to selected titles and update state accordingly
  const attributionSelectedTitlesUpdated = function(e) {
    setSelectedTitles(
      JSON.parse(localStorage.getItem('attributionSelectedTitles'))
    );
  };
  document.addEventListener(
    'attributionSelectedTitlesUpdated',
    attributionSelectedTitlesUpdated,
    false
  );

  const attributionPlatformParams = params => {
    localStorage.setItem('attributionPlatformParams', params);
  };

  // When the title view button is clicked
  const loadTitleViewForSelected = () => {
    const titleParam = selectedTitles.join('%7C');
    const params = window.location.search.replace('?', '');
    // adding filter params to title view while removing the platform filter if exists.
    const filterParams = params.split('&');
    for (let i = 0; i < filterParams.length; i += 1) {
      if (filterParams[i].indexOf('=platform') !== -1) {
        filterParams.splice(i, 1);
      }
    }
    const paramsNoPlatform = filterParams.join('&');

    attributionPlatformParams(params);
    window.location = `/attribution/title?filters%5B0%5D=title%3A${titleParam}&filters%5B1%5D=${paramsNoPlatform}`;
  };

  return (
    <>
      <TitleBanner>
        <PageHeader
          title="Platform Attribution"
          tooltip="The Platform Attribution tool lets you see where viewers watched at both the platform and title level. You can use the toggle to switch between views. TV Time began collecting this data in June 2020, so the all-time range for this tool is from June 2020 to the present. TV Time users indicate where they watched a title, or they can choose from options like Other, Unofficial, Originating Network (for TV shows), or Theater (for Movies, added February 2022). Consider these factors when using this dataset."
        />
      </TitleBanner>

      <div className="tvt-platform-attribution__viewtoggle">
        <div>Platform View</div>
        <div className="tvt-platform-attribution__divider">|</div>
        <div>
          <a href="/attribution/title">Title View</a>
        </div>
      </div>

      <hr />

      {!showTable && (
        <>
          <section className="tvt-main__content">
            <AttributionFilters
              filterFields={filters.fields}
              openSegment={openAudienceSegmentWindow}
              showTable={showTable}
              onApply={updateFilters}
              onReset={clearFilters}
            />
          </section>
        </>
      )}

      {showTable && (
        <>
          <AttributionFilters
            filterFields={filters.fields}
            showTable={showTable}
            onApply={updateFilters}
            onReset={clearFilters}
          />

          <Button
            className="tvt-attribution-platform__title-viewership-button"
            disabled={selectedTitles.length === 0}
            onClick={loadTitleViewForSelected}
          >
            View Selected Titles
          </Button>

          <div className="tvt-attribution-platform__charts-wrapper">
            <InlineQuery useChildForLoading query={queries.attributionPlatform}>
              {query => (
                <Table
                  data={query.data}
                  headerFormatter={columns}
                  defaultSorts={defaultSorts}
                  view="attributionPlatform"
                  height={438}
                  filters={filters.fields}
                  showRank={false}
                  queryMeta={
                    config.ATTRIBUTION_PLATFORM_FILTERS.queries
                      .attributionPlatform
                  }
                />
              )}
            </InlineQuery>
          </div>
        </>
      )}
    </>
  );
};

export default AttributionPlatform;
